import type {
  CreateThreadRequest,
  CreateThreadResponse,
  GetThreadsParams,
  MessageStateResponse,
  Thread,
  ThreadMessage,
  UpdateThreadRequest,
} from '@/interfaces/thread.interface';
import type { AskUserReply } from '@/types/tools';
import { logger } from '@/utils/logger';
import api from './base.api';

const reasonerProjectThreadsBasePath = (projectId: string) =>
  `/reasoner/projects/${projectId}/threads`;

export const getThreads = async ({
  projectId,
  userId,
  limit = 100,
  offset = 0,
}: GetThreadsParams = {}) => {
  if (projectId == null) {
    throw new Error('getThreads requires projectId');
  }
  const params: any = { limit, offset };
  if (userId) {
    params.user_id = userId;
  }

  return api.get<Thread[]>(reasonerProjectThreadsBasePath(projectId), {
    params,
  });
};

export const createThread = async (
  projectId: string,
  data: CreateThreadRequest,
) => {
  return api.post<CreateThreadResponse>(
    reasonerProjectThreadsBasePath(projectId),
    data,
  );
};

export const deleteThread = async (projectId: string, threadId: string) => {
  return api.delete(`${reasonerProjectThreadsBasePath(projectId)}/${threadId}`);
};

export const updateThread = async (
  projectId: string,
  threadId: string,
  data: UpdateThreadRequest,
) => {
  try {
    const response = await api.put<Thread>(
      `${reasonerProjectThreadsBasePath(projectId)}/${threadId}`,
      data,
    );
    return response.data;
  } catch (error) {
    logger.error('API Error: updateThread', {
      projectId,
      threadId,
      error,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

export const getThreadMessages = async (
  projectId: string,
  threadId: string,
): Promise<ThreadMessage[]> => {
  try {
    const response = await api.get<ThreadMessage[]>(
      `${reasonerProjectThreadsBasePath(projectId)}/${threadId}/messages`,
    );
    return response.data;
  } catch (error) {
    logger.error('API Error: getThreadMessages', {
      projectId,
      threadId,
      error,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Fetch current accumulated message state from Redis.
 *
 * This endpoint returns the current streaming state for reconnection,
 * including content built up so far and the lastEventId for SSE resume.
 */
export const getMessageState = async (
  projectId: string,
  threadId: string,
  messageId: string,
): Promise<MessageStateResponse> => {
  try {
    const response = await api.get<MessageStateResponse>(
      `/reasoner/projects/${projectId}/threads/${threadId}/messages/${messageId}/state`,
    );
    return response.data;
  } catch (error) {
    logger.error('API Error: getMessageState', {
      projectId,
      threadId,
      messageId,
      error,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Hand the user's reply to the ask_user call `toolCallId` that waits in the
 * thread's running turn; the turn carries on with it as the tool result.
 * 409 means no call waits for it (answered, expired or not started yet).
 */
export const replyToToolCall = async (
  projectId: string,
  threadId: string,
  toolCallId: string,
  reply: AskUserReply,
): Promise<void> => {
  // The id comes from the model provider, not from us.
  const toolCall = encodeURIComponent(toolCallId);
  await api.post(
    `${reasonerProjectThreadsBasePath(projectId)}/${threadId}/tool-calls/${toolCall}/reply`,
    reply,
  );
};
